/**
 * Numeric critical-point finder and extrema classifier for single-variable functions.
 *
 * - `criticalPoints`: finds roots of f′ over [x0, x1] by sign-change bracketing + bisection.
 * - `extremaFromCritical`: classifies each root via the second-derivative test.
 */

export interface OptimizeOptions {
  /** Number of scan steps; more steps catch roots closer together. Default 400. */
  steps?: number;
  /** Bisection tolerance — stops when the bracket is narrower than this. Default 1e-9. */
  tolerance?: number;
}

export interface Extremum {
  x: number;
  y: number;
  kind: 'min' | 'max' | 'inflection';
}

/**
 * Find roots of `fPrime` over `[x0, x1]` by scanning for sign changes and refining
 * each bracket with bisection.
 *
 * Edge cases:
 * - A root that sits exactly on a sample point (fPrime = 0 at a grid node) may be
 *   detected as two adjacent brackets or missed entirely; the bisection still converges
 *   because the value at the exact node is zero and both neighbouring brackets collapse
 *   toward it.
 * - Near-flat regions where |fPrime| < ε at every sample point but no true sign change
 *   occurs are NOT returned as roots (correctly skipped — they are not extrema).
 * - Duplicate roots that are within `tolerance` of each other (from adjacent sign-change
 *   brackets around a tangent zero) are de-duplicated: only one representative is kept.
 */
export function criticalPoints(
  fPrime: (x: number) => number,
  x0: number,
  x1: number,
  opts?: OptimizeOptions,
): number[] {
  const steps = opts?.steps ?? 400;
  const tol = opts?.tolerance ?? 1e-9;

  const dx = (x1 - x0) / steps;
  const roots: number[] = [];

  let prevX = x0;
  let prevY = fPrime(x0);

  for (let i = 1; i <= steps; i++) {
    const currX = x0 + i * dx;
    const currY = fPrime(currX);

    // Sign change detected (or exact zero at currX).
    if (prevY * currY < 0 || currY === 0) {
      const root = currY === 0 ? currX : bisect(fPrime, prevX, currX, tol);
      // De-duplicate: skip if too close to the last found root.
      if (roots.length === 0 || Math.abs(root - roots[roots.length - 1]!) > tol * 10) {
        roots.push(root);
      }
    }

    prevX = currX;
    prevY = currY;
  }

  return roots;
}

/**
 * Bisect `f` over `[a, b]` (assumed to bracket a sign change) to find a root within
 * `tolerance`.
 */
function bisect(f: (x: number) => number, a: number, b: number, tolerance: number): number {
  let lo = a;
  let hi = b;
  // Ensure lo < hi for clarity (the algorithm doesn't require a sign convention).
  if (lo > hi) {
    [lo, hi] = [hi, lo];
  }

  for (let iter = 0; iter < 100; iter++) {
    const mid = (lo + hi) / 2;
    if (hi - lo < tolerance) {
      return mid;
    }
    const fMid = f(mid);
    if (fMid === 0) {
      return mid;
    }
    if (f(lo) * fMid < 0) {
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return (lo + hi) / 2;
}

/**
 * Classify critical points using the second-derivative test.
 *
 * For each `x` in `crit`:
 * - `fDoublePrime(x) > 0` → local minimum
 * - `fDoublePrime(x) < 0` → local maximum
 * - `fDoublePrime(x) ≈ 0` → inconclusive; reported as `'inflection'`
 *
 * The `fPrime` parameter is accepted for API symmetry and future higher-order tests
 * but is not used in the current second-derivative classification.
 */
export function extremaFromCritical(
  f: (x: number) => number,
  _fPrime: (x: number) => number,
  fDoublePrime: (x: number) => number,
  crit: number[],
): Extremum[] {
  const secondDerivThreshold = 1e-10;
  return crit.map((x) => {
    const fpp = fDoublePrime(x);
    let kind: 'min' | 'max' | 'inflection';
    if (fpp > secondDerivThreshold) {
      kind = 'min';
    } else if (fpp < -secondDerivThreshold) {
      kind = 'max';
    } else {
      kind = 'inflection';
    }
    return { x, y: f(x), kind };
  });
}
